import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import ImavenLoadingScreen from '../../../Components/Helpers/loading-screen';
import ToastrService from '../../../services/toaster.service';

// ============================================
// SVG ICONS
// ============================================

const Icons = {
  strategy: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  trash: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>,
  save: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>,
  copy: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
  lightbulb: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.9V17a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-2.1A7 7 0 0 0 12 2z"/></svg>,
  question: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  trigger: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  action: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
};

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  gap: 0;
`;

const Sidebar = styled.div`
  width: 350px;
  min-width: 350px;
  height: 100%;
  background: rgba(15, 20, 35, 0.95);
  border-right: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
`;

const SidebarTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  color: #fff;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const StrategyList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const StrategyCard = styled.div`
  background: ${props => props.$selected 
    ? 'rgba(99, 102, 241, 0.2)' 
    : 'rgba(25, 30, 45, 0.6)'};
  border: 1px solid ${props => props.$selected 
    ? 'rgba(99, 102, 241, 0.5)' 
    : 'rgba(99, 102, 241, 0.15)'};
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(99, 102, 241, 0.4);
    background: rgba(99, 102, 241, 0.1);
  }
`;

const StrategyCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const StrategyCardTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
`;

const StatusBadge = styled.span`
  font-size: 10px;
  padding: 3px 8px;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => props.$status === 'active' && `
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  `}
  
  ${props => props.$status === 'draft' && `
    background: rgba(251, 191, 36, 0.15);
    color: #fbbf24;
  `}
  
  ${props => props.$status === 'archived' && `
    background: rgba(100, 100, 100, 0.15);
    color: #888;
  `}
`;

const StrategyCardMeta = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  gap: 12px;
`;

const CreateButton = styled.button`
  width: 100%;
  padding: 12px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;

  &:hover {
    opacity: 0.9;
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ContentTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  background: ${props => props.$primary 
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
    : 'rgba(30, 35, 50, 0.8)'};
  border: 1px solid ${props => props.$primary 
    ? 'transparent' 
    : 'rgba(99, 102, 241, 0.3)'};
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const FormSection = styled.div`
  background: rgba(20, 25, 40, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  option {
    background: #1a1f2e;
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ItemCard = styled.div`
  background: rgba(30, 35, 50, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.1);
  border-radius: 8px;
  padding: 12px;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const ItemTitle = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #a5b4fc;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.8;
  }
`;

const AddButton = styled.button`
  padding: 10px;
  background: rgba(99, 102, 241, 0.1);
  border: 1px dashed rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  color: #a5b4fc;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: rgba(99, 102, 241, 0.2);
  }
`;

// Validation Modal Overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ValidationModal = styled.div`
  background: linear-gradient(145deg, #1a1f35 0%, #0f1420 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  padding: 24px;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const ModalIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  
  ${props => props.$type === 'error' && `
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
  `}
  
  ${props => props.$type === 'warning' && `
    background: rgba(251, 191, 36, 0.15);
    color: #fbbf24;
  `}
  
  ${props => props.$type === 'success' && `
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
  `}
`;

const ModalTitle = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const ModalSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  margin: 4px 0 0 0;
`;

const ValidationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
`;

const ValidationItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: ${props => props.$complete 
    ? 'rgba(16, 185, 129, 0.1)' 
    : 'rgba(239, 68, 68, 0.1)'};
  border: 1px solid ${props => props.$complete 
    ? 'rgba(16, 185, 129, 0.2)' 
    : 'rgba(239, 68, 68, 0.2)'};
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 13px;
  color: ${props => props.$complete ? '#10b981' : '#ef4444'};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ValidationIcon = styled.span`
  font-size: 16px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.$primary && `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: white;
    
    &:hover {
      opacity: 0.9;
    }
  `}
  
  ${props => props.$success && `
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    border: none;
    color: white;
    
    &:hover {
      opacity: 0.9;
    }
  `}
  
  ${props => !props.$primary && !props.$success && `
    background: transparent;
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: rgba(255, 255, 255, 0.7);
    
    &:hover {
      border-color: rgba(99, 102, 241, 0.5);
      color: white;
    }
  `}
`;

// Required field indicator
const RequiredIndicator = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

// Field wrapper with validation state
const ValidatedFormGroup = styled(FormGroup)`
  ${props => props.$hasError && `
    & > input, & > textarea, & > select {
      border-color: rgba(239, 68, 68, 0.5) !important;
      box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.1);
    }
  `}
`;

// Completion Progress Bar
const ProgressBar = styled.div`
  height: 6px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 3px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => {
    if (props.$percent === 100) return 'linear-gradient(90deg, #10b981, #059669)';
    if (props.$percent >= 60) return 'linear-gradient(90deg, #fbbf24, #f59e0b)';
    return 'linear-gradient(90deg, #ef4444, #dc2626)';
  }};
  width: ${props => props.$percent}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
  padding: 40px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  margin: 0 0 8px 0;
`;

const TagsInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 8px;
  min-height: 42px;
`;

const Tag = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(99, 102, 241, 0.2);
  border-radius: 4px;
  font-size: 12px;
  color: #a5b4fc;

  button {
    background: none;
    border: none;
    color: #a5b4fc;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
    line-height: 1;
  }
`;

const TagInput = styled.input`
  flex: 1;
  min-width: 100px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 13px;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

// ============================================
// COMPONENT
// ============================================

const ExpertStrategiesModule = () => {
  const axios = useAuthenticatedAxios();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(null);
  const [tagInput, setTagInput] = useState('');
  const [validationModal, setValidationModal] = useState(null); // { type, validations }

  // ============================================
  // VALIDATION LOGIC
  // ============================================
  
  const validateStrategy = (data) => {
    const validations = [
      { 
        field: 'name', 
        label: 'Strategy Name', 
        complete: Boolean(data?.name?.trim()),
        required: true
      },
      { 
        field: 'description', 
        label: 'Description', 
        complete: Boolean(data?.description?.trim()),
        required: true
      },
      { 
        field: 'category', 
        label: 'Category', 
        complete: Boolean(data?.category?.trim()),
        required: true
      },
      { 
        field: 'expertInsight', 
        label: 'Expert Insight', 
        complete: Boolean(data?.expertInsight?.trim()),
        required: true
      },
      { 
        field: 'triggers', 
        label: 'At least 1 Trigger', 
        complete: data?.triggers?.length > 0,
        required: true
      },
      { 
        field: 'actions', 
        label: 'At least 1 Action', 
        complete: data?.actions?.length > 0,
        required: false  // Recommended but not required
      },
      { 
        field: 'discoveryQuestions', 
        label: 'Discovery Questions', 
        complete: data?.discoveryQuestions?.length > 0,
        required: false  // Recommended but not required
      }
    ];
    
    const requiredComplete = validations.filter(v => v.required).every(v => v.complete);
    const allComplete = validations.every(v => v.complete);
    const completionPercent = Math.round((validations.filter(v => v.complete).length / validations.length) * 100);
    
    return { validations, requiredComplete, allComplete, completionPercent };
  };
  
  const getCompletionPercent = () => {
    if (!formData) return 0;
    const { completionPercent } = validateStrategy(formData);
    return completionPercent;
  };

  // ============================================
  // DATA FETCHING
  // ============================================

  const fetchStrategies = async (autoSelectFirst = false) => {
    try {
      setLoading(true);
      const response = await axios.get('/api/expert-strategies');
      if (response.data.success) {
        const loadedStrategies = response.data.strategies || [];
        setStrategies(loadedStrategies);
        
        // Auto-select first strategy if requested and we have strategies but none selected
        if (autoSelectFirst && loadedStrategies.length > 0 && !selectedStrategy) {
          setSelectedStrategy(loadedStrategies[0]);
          setFormData({ ...loadedStrategies[0] });
        }
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
      ToastrService.Error('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStrategies(true);  // Auto-select first on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // FORM MANAGEMENT
  // ============================================

  const createNewStrategy = () => {
    const newStrategy = {
      name: '',
      description: '',
      triggers: [],
      triggerLogic: 'OR',
      discoveryQuestions: [],
      expertInsight: '',
      actions: [],
      category: 'immigration',
      tags: [],
      relatedPathways: [],
      priority: 0,
      status: 'draft'
    };
    setFormData(newStrategy);
    setSelectedStrategy(null);
  };

  const selectStrategy = (strategy) => {
    setSelectedStrategy(strategy);
    setFormData({ ...strategy });
  };

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ============================================
  // TRIGGERS MANAGEMENT
  // ============================================

  const addTrigger = () => {
    const newTrigger = {
      type: 'keyword',
      value: 'New trigger - please add keywords',  // Default value to pass validation
      keywords: []
    };
    updateFormField('triggers', [...(formData.triggers || []), newTrigger]);
  };

  const updateTrigger = (index, field, value) => {
    const updated = [...formData.triggers];
    updated[index] = { ...updated[index], [field]: value };
    updateFormField('triggers', updated);
  };

  const removeTrigger = (index) => {
    updateFormField('triggers', formData.triggers.filter((_, i) => i !== index));
  };

  // ============================================
  // DISCOVERY QUESTIONS MANAGEMENT
  // ============================================

  const addDiscoveryQuestion = () => {
    const newQuestion = {
      question: '',
      purpose: ''
    };
    updateFormField('discoveryQuestions', [...(formData.discoveryQuestions || []), newQuestion]);
  };

  const updateDiscoveryQuestion = (index, field, value) => {
    const updated = [...formData.discoveryQuestions];
    updated[index] = { ...updated[index], [field]: value };
    updateFormField('discoveryQuestions', updated);
  };

  const removeDiscoveryQuestion = (index) => {
    updateFormField('discoveryQuestions', formData.discoveryQuestions.filter((_, i) => i !== index));
  };

  // ============================================
  // ACTIONS MANAGEMENT
  // ============================================

  const addAction = () => {
    const newAction = {
      type: 'suggest_alternative',
      action: '',
      userMessage: ''
    };
    updateFormField('actions', [...(formData.actions || []), newAction]);
  };

  const updateAction = (index, field, value) => {
    const updated = [...formData.actions];
    updated[index] = { ...updated[index], [field]: value };
    updateFormField('actions', updated);
  };

  const removeAction = (index) => {
    updateFormField('actions', formData.actions.filter((_, i) => i !== index));
  };

  // ============================================
  // TAGS MANAGEMENT
  // ============================================

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        updateFormField('tags', [...formData.tags, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    updateFormField('tags', formData.tags.filter(t => t !== tag));
  };

  // ============================================
  // SAVE & DELETE
  // ============================================

  const saveStrategy = async (forceActivate = false) => {
    // Validate first
    const validation = validateStrategy(formData);
    
    // Check if required fields are missing
    if (!validation.requiredComplete) {
      setValidationModal({
        type: 'error',
        title: 'Required Fields Missing',
        subtitle: 'Please complete all required fields before saving',
        validations: validation.validations,
        showActivateButton: false
      });
      return;
    }
    
    // If all complete but still draft, prompt to activate
    if (validation.allComplete && formData.status === 'draft' && !forceActivate) {
      setValidationModal({
        type: 'success',
        title: 'Strategy Complete!',
        subtitle: 'All fields are filled. Would you like to activate this strategy?',
        validations: validation.validations,
        showActivateButton: true
      });
      return;
    }

    try {
      setSaving(true);
      
      // Auto-fix triggers with missing 'value' field (required by schema)
      const fixedFormData = {
        ...formData,
        status: forceActivate ? 'active' : formData.status,
        triggers: (formData.triggers || []).map(trigger => ({
          ...trigger,
          value: trigger.value || 
            (trigger.type === 'keyword' && trigger.keywords?.length > 0 
              ? `Keywords: ${trigger.keywords.slice(0, 3).join(', ')}`
              : trigger.type || 'trigger')
        })),
        // Auto-fix actions with missing 'action' field
        actions: (formData.actions || []).map(action => ({
          ...action,
          action: action.action || action.userMessage || 'Action to be defined'
        })),
        // Auto-fix discovery questions with missing 'purpose' field
        discoveryQuestions: (formData.discoveryQuestions || []).map(q => ({
          ...q,
          purpose: q.purpose || 'To gather more information'
        }))
      };
      
      let response;
      if (selectedStrategy?._id) {
        response = await axios.put(`/api/expert-strategies/${selectedStrategy._id}`, fixedFormData);
      } else {
        response = await axios.post('/api/expert-strategies', fixedFormData);
      }

      if (response.data.success) {
        ToastrService.Success(forceActivate 
          ? 'Strategy saved and activated!' 
          : 'Strategy saved successfully');
        fetchStrategies();
        setValidationModal(null);
        if (!selectedStrategy?._id) {
          setSelectedStrategy(response.data.strategy);
        } else if (forceActivate) {
          // Update local formData with new status
          setFormData(prev => ({ ...prev, status: 'active' }));
        }
      }
    } catch (error) {
      console.error('Error saving strategy:', error);
      
      // Extract detailed error message from response
      const errorData = error.response?.data;
      let errorMessage = 'Failed to save strategy';
      
      if (errorData?.details) {
        // Validation errors - show which fields failed
        const fieldErrors = Object.entries(errorData.details)
          .map(([field, msg]) => {
            // Make field names more readable
            const readableField = field
              .replace(/\.\d+\./g, ' → ')  // triggers.0.value → triggers → value
              .replace(/([A-Z])/g, ' $1')  // camelCase → Camel Case
              .trim();
            return `• ${readableField}: ${msg}`;
          })
          .join(' | ');
        errorMessage = `Validation failed: ${fieldErrors}`;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      }
      
      ToastrService.Error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const deleteStrategy = async () => {
    if (!selectedStrategy?._id) return;
    
    if (!window.confirm('Are you sure you want to delete this strategy?')) return;

    try {
      const response = await axios.delete(`/api/expert-strategies/${selectedStrategy._id}`);
      if (response.data.success) {
        ToastrService.Success('Strategy deleted');
        setSelectedStrategy(null);
        setFormData(null);
        fetchStrategies();
      }
    } catch (error) {
      console.error('Error deleting strategy:', error);
      ToastrService.Error('Failed to delete strategy');
    }
  };

  const duplicateStrategy = async () => {
    if (!selectedStrategy?._id) return;

    try {
      const response = await axios.post(`/api/expert-strategies/${selectedStrategy._id}/duplicate`);
      if (response.data.success) {
        ToastrService.Success('Strategy duplicated');
        fetchStrategies();
        selectStrategy(response.data.strategy);
      }
    } catch (error) {
      console.error('Error duplicating strategy:', error);
      ToastrService.Error('Failed to duplicate strategy');
    }
  };

  // ============================================
  // FILTERING
  // ============================================

  const filteredStrategies = strategies.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return <ImavenLoadingScreen text="Loading Expert Strategies..." />;
  }

  return (
    <>
      {/* Validation Modal */}
      {validationModal && (
        <ModalOverlay onClick={() => setValidationModal(null)}>
          <ValidationModal onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalIcon $type={validationModal.type}>
                {validationModal.type === 'error' ? '⚠️' : 
                 validationModal.type === 'success' ? '✓' : 'ℹ️'}
              </ModalIcon>
              <div>
                <ModalTitle>{validationModal.title}</ModalTitle>
                <ModalSubtitle>{validationModal.subtitle}</ModalSubtitle>
              </div>
            </ModalHeader>
            
            <ValidationList>
              {validationModal.validations
                .filter(v => v.required || !v.complete)  // Show required + incomplete optional
                .map((v, i) => (
                  <ValidationItem key={i} $complete={v.complete}>
                    <ValidationIcon>{v.complete ? '✓' : '✗'}</ValidationIcon>
                    {v.label}
                    {v.required && !v.complete && <RequiredIndicator>*</RequiredIndicator>}
                  </ValidationItem>
                ))
              }
            </ValidationList>
            
            <ModalActions>
              <ModalButton onClick={() => setValidationModal(null)}>
                {validationModal.showActivateButton ? 'Keep as Draft' : 'Close'}
              </ModalButton>
              {validationModal.showActivateButton && (
                <ModalButton $success onClick={() => saveStrategy(true)}>
                  Save & Activate
                </ModalButton>
              )}
              {validationModal.type === 'error' && (
                <ModalButton $primary onClick={() => setValidationModal(null)}>
                  Fix Issues
                </ModalButton>
              )}
            </ModalActions>
          </ValidationModal>
        </ModalOverlay>
      )}
    
    <ModuleContainer>
      {/* Sidebar - Strategy List */}
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>
            {Icons.strategy} Expert Strategies
          </SidebarTitle>
          <SearchInput
            placeholder="Search strategies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <CreateButton onClick={createNewStrategy}>
            {Icons.plus} New Strategy
          </CreateButton>
        </SidebarHeader>

        <StrategyList>
          {filteredStrategies.map(strategy => (
            <StrategyCard
              key={strategy._id}
              $selected={selectedStrategy?._id === strategy._id}
              onClick={() => selectStrategy(strategy)}
            >
              <StrategyCardHeader>
                <StrategyCardTitle>{strategy.name}</StrategyCardTitle>
                <StatusBadge $status={strategy.status}>{strategy.status}</StatusBadge>
              </StrategyCardHeader>
              <StrategyCardMeta>
                <span>🏷️ {strategy.category}</span>
                <span>⚡ {strategy.triggers?.length || 0} triggers</span>
                <span>📊 {strategy.stats?.successRate || 0}% success</span>
              </StrategyCardMeta>
            </StrategyCard>
          ))}

          {filteredStrategies.length === 0 && (
            <EmptyState>
              <EmptyStateText>No strategies found</EmptyStateText>
            </EmptyState>
          )}
        </StrategyList>
      </Sidebar>

      {/* Main Content - Strategy Editor */}
      <MainContent>
        {formData ? (
          <>
            <ContentHeader>
              <ContentTitle>
                {selectedStrategy?._id ? 'Edit Strategy' : 'New Strategy'}
              </ContentTitle>
              <HeaderActions>
                {selectedStrategy?._id && (
                  <>
                    <ActionButton onClick={duplicateStrategy}>
                      {Icons.copy} Duplicate
                    </ActionButton>
                    <ActionButton onClick={deleteStrategy}>
                      {Icons.trash} Delete
                    </ActionButton>
                  </>
                )}
                <ActionButton $primary onClick={saveStrategy} disabled={saving}>
                  {Icons.save} {saving ? 'Saving...' : 'Save'}
                </ActionButton>
              </HeaderActions>
            </ContentHeader>

            <ContentBody>
              {/* Completion Progress */}
              <ProgressText>
                <span>Strategy Completion</span>
                <span>{getCompletionPercent()}% complete</span>
              </ProgressText>
              <ProgressBar>
                <ProgressFill $percent={getCompletionPercent()} />
              </ProgressBar>
              
              {/* Basic Info */}
              <FormSection>
                <SectionTitle>{Icons.lightbulb} Basic Information</SectionTitle>
                <FormGroup>
                  <Label>Strategy Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => updateFormField('name', e.target.value)}
                    placeholder="e.g., Points Gap - Marriage Opportunity"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Description</Label>
                  <TextArea
                    value={formData.description}
                    onChange={(e) => updateFormField('description', e.target.value)}
                    placeholder="Describe when and why this strategy is used..."
                  />
                </FormGroup>
                <FormGroup style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <Label>Category</Label>
                    <Select
                      value={formData.category}
                      onChange={(e) => updateFormField('category', e.target.value)}
                    >
                      <option value="immigration">Immigration</option>
                      <option value="financial">Financial</option>
                      <option value="startup">Startup</option>
                      <option value="health">Health</option>
                    </Select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Label>Status</Label>
                    <Select
                      value={formData.status}
                      onChange={(e) => updateFormField('status', e.target.value)}
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </Select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <Label>Priority</Label>
                    <Input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => updateFormField('priority', parseInt(e.target.value) || 0)}
                      placeholder="0"
                    />
                  </div>
                </FormGroup>
                <FormGroup>
                  <Label>Tags</Label>
                  <TagsInput>
                    {formData.tags?.map(tag => (
                      <Tag key={tag}>
                        {tag}
                        <button onClick={() => removeTag(tag)}>×</button>
                      </Tag>
                    ))}
                    <TagInput
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder="Add tag and press Enter..."
                    />
                  </TagsInput>
                </FormGroup>
              </FormSection>

              {/* Triggers */}
              <FormSection>
                <SectionTitle>{Icons.trigger} Triggers (When to Activate)</SectionTitle>
                <FormGroup>
                  <Label>Trigger Logic</Label>
                  <Select
                    value={formData.triggerLogic}
                    onChange={(e) => updateFormField('triggerLogic', e.target.value)}
                    style={{ width: '200px' }}
                  >
                    <option value="OR">ANY trigger matches (OR)</option>
                    <option value="AND">ALL triggers match (AND)</option>
                  </Select>
                </FormGroup>
                <ItemList>
                  {formData.triggers?.map((trigger, index) => (
                    <ItemCard key={index}>
                      <ItemHeader>
                        <ItemTitle>Trigger {index + 1}</ItemTitle>
                        <RemoveButton onClick={() => removeTrigger(index)}>
                          {Icons.trash}
                        </RemoveButton>
                      </ItemHeader>
                      <FormGroup>
                        <Label>Type</Label>
                        <Select
                          value={trigger.type}
                          onChange={(e) => updateTrigger(index, 'type', e.target.value)}
                        >
                          <option value="keyword">Keyword in Message</option>
                          <option value="obstacle">Obstacle Detected</option>
                          <option value="pathway_blocked">Pathway Blocked</option>
                          <option value="user_statement">User Statement Contains</option>
                          <option value="missing_requirement">Missing Requirement</option>
                        </Select>
                      </FormGroup>
                      <FormGroup>
                        <Label>Value / Keywords (comma-separated for multiple)</Label>
                        <Input
                          value={trigger.type === 'keyword' ? trigger.keywords?.join(', ') : trigger.value}
                          onChange={(e) => {
                            if (trigger.type === 'keyword') {
                              const keywords = e.target.value.split(',').map(k => k.trim());
                              // Update both keywords AND value (value is required by schema)
                              const updated = [...formData.triggers];
                              updated[index] = { 
                                ...updated[index], 
                                keywords,
                                value: keywords.length > 0 ? `Keywords: ${keywords.slice(0, 3).join(', ')}` : ''
                              };
                              updateFormField('triggers', updated);
                            } else {
                              updateTrigger(index, 'value', e.target.value);
                            }
                          }}
                          placeholder={trigger.type === 'keyword' 
                            ? "e.g., not enough points, can't qualify, don't meet" 
                            : "e.g., crs_points_insufficient"}
                        />
                      </FormGroup>
                    </ItemCard>
                  ))}
                  <AddButton onClick={addTrigger}>
                    {Icons.plus} Add Trigger
                  </AddButton>
                </ItemList>
              </FormSection>

              {/* Discovery Questions */}
              <FormSection>
                <SectionTitle>{Icons.question} Discovery Questions (What to Ask)</SectionTitle>
                <ItemList>
                  {formData.discoveryQuestions?.map((question, index) => (
                    <ItemCard key={index}>
                      <ItemHeader>
                        <ItemTitle>Question {index + 1}</ItemTitle>
                        <RemoveButton onClick={() => removeDiscoveryQuestion(index)}>
                          {Icons.trash}
                        </RemoveButton>
                      </ItemHeader>
                      <FormGroup>
                        <Label>Question to Ask</Label>
                        <Input
                          value={question.question}
                          onChange={(e) => updateDiscoveryQuestion(index, 'question', e.target.value)}
                          placeholder="e.g., Are you currently married?"
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Purpose (Why ask this?)</Label>
                        <Input
                          value={question.purpose}
                          onChange={(e) => updateDiscoveryQuestion(index, 'purpose', e.target.value)}
                          placeholder="e.g., Check for spousal sponsorship opportunity"
                        />
                      </FormGroup>
                    </ItemCard>
                  ))}
                  <AddButton onClick={addDiscoveryQuestion}>
                    {Icons.plus} Add Discovery Question
                  </AddButton>
                </ItemList>
              </FormSection>

              {/* Expert Insight */}
              <FormSection>
                <SectionTitle>{Icons.lightbulb} Expert Insight (The "Trick") *</SectionTitle>
                <FormGroup>
                  <Label>What does a good lawyer/expert know that others don't?</Label>
                  <TextArea
                    value={formData.expertInsight}
                    onChange={(e) => updateFormField('expertInsight', e.target.value)}
                    placeholder="e.g., If the user is single, this is actually an OPPORTUNITY - they can explore spousal sponsorship routes, international marriage agencies, or provincial programs that favor single applicants..."
                    style={{ minHeight: '120px' }}
                  />
                </FormGroup>
              </FormSection>

              {/* Actions */}
              <FormSection>
                <SectionTitle>{Icons.action} Actions (What to Do)</SectionTitle>
                <ItemList>
                  {formData.actions?.map((action, index) => (
                    <ItemCard key={index}>
                      <ItemHeader>
                        <ItemTitle>Action {index + 1}</ItemTitle>
                        <RemoveButton onClick={() => removeAction(index)}>
                          {Icons.trash}
                        </RemoveButton>
                      </ItemHeader>
                      <FormGroup>
                        <Label>Action Type</Label>
                        <Select
                          value={action.type}
                          onChange={(e) => updateAction(index, 'type', e.target.value)}
                        >
                          <option value="suggest_alternative">Suggest Alternative Pathway</option>
                          <option value="pivot_pathway">Pivot to Different Pathway</option>
                          <option value="request_document">Request Document</option>
                          <option value="explore_option">Explore Option</option>
                          <option value="reframe_situation">Reframe the Situation</option>
                        </Select>
                      </FormGroup>
                      <FormGroup>
                        <Label>Action Description</Label>
                        <TextArea
                          value={action.action}
                          onChange={(e) => updateAction(index, 'action', e.target.value)}
                          placeholder="Describe what the AI should do..."
                          style={{ minHeight: '60px' }}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>How to Present to User (optional)</Label>
                        <TextArea
                          value={action.userMessage}
                          onChange={(e) => updateAction(index, 'userMessage', e.target.value)}
                          placeholder="How should the AI phrase this to the user..."
                          style={{ minHeight: '60px' }}
                        />
                      </FormGroup>
                    </ItemCard>
                  ))}
                  <AddButton onClick={addAction}>
                    {Icons.plus} Add Action
                  </AddButton>
                </ItemList>
              </FormSection>
            </ContentBody>
          </>
        ) : strategies.length > 0 ? (
          <EmptyState>
            <EmptyStateIcon>👈</EmptyStateIcon>
            <EmptyStateText style={{ fontSize: '18px', color: '#fff' }}>
              Select a Strategy
            </EmptyStateText>
            <EmptyStateText>
              Click on a strategy from the list to view and edit it, <br />
              or create a new one.
            </EmptyStateText>
            <CreateButton style={{ marginTop: '20px', width: 'auto', padding: '12px 24px' }} onClick={createNewStrategy}>
              {Icons.plus} Create New Strategy
            </CreateButton>
          </EmptyState>
        ) : (
          <EmptyState>
            <EmptyStateIcon>🎯</EmptyStateIcon>
            <EmptyStateText style={{ fontSize: '18px', color: '#fff' }}>
              Expert Strategies
            </EmptyStateText>
            <EmptyStateText>
              Create strategies that capture "what a good lawyer would do" - <br />
              the creative workarounds and alternative paths that make the difference.
            </EmptyStateText>
            <CreateButton style={{ marginTop: '20px', width: 'auto', padding: '12px 24px' }} onClick={createNewStrategy}>
              {Icons.plus} Create Your First Strategy
            </CreateButton>
          </EmptyState>
        )}
      </MainContent>
    </ModuleContainer>
    </>
  );
};

export default ExpertStrategiesModule;
